import { Fragment, useEffect, useState } from "react";
import type { CampaignCostsResponse, CostLine, SessionCost, UpdateStatus } from "@dm/shared";
import { api } from "./api.js";
import { NextSessionNote } from "./Schedule.js";
import { useGame } from "./game.js";

export const usd = (n: number) => (n > 0 && n < 0.01 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`);
const SOURCE_LABELS: Record<string, string> = {
  dm: "DM replies", tts: "DM speech", scribe: "Memory scribe", whisper: "Whisper replies", preview: "Voice previews", test: "Key tests",
  levelup: "Level-up help", import: "Character import", builder: "Adventure Builder", help: "Help assistant", image: "Scene images",
  spec: "Speculative drafts (unused)", think: "Background thinking",
};
const tokens = (l: CostLine) => (l.images ? `${l.images} image${l.images === 1 ? "" : "s"}` : l.characters ? `${l.characters.toLocaleString()} chars` : `${(l.inputTokens + l.outputTokens).toLocaleString()} tokens`);

/** Cost per kind of call. Staff see the actual cost and the player price; players see the player price. */
export function CostLines({ s }: { s: SessionCost }) {
  const both = !!s.staff && s.staff.actualUsd !== s.totalUsd;
  return (
    <table className="simple">
      {both && <thead><tr><th /><th /><th>Actual</th><th>Players pay</th></tr></thead>}
      <tbody>
        {s.lines.map((l) => (
          <tr key={`${l.source}-${l.model}`}>
            <td>{SOURCE_LABELS[l.source] ?? l.source}</td>
            <td className="muted small">{l.model} · {l.calls} call{l.calls === 1 ? "" : "s"} · {tokens(l)}</td>
            {both && <td>{usd(l.actualUsd ?? 0)}</td>}
            <td>{usd(l.usd)}{l.unpriced && <span className="tag danger" title="No price in the table for this model">unpriced</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Host / Co-host: the markup players are charged (actual cost x multiplier). */
function MultiplierEditor({ value, onSaved }: { value: number; onSaved: () => void }) {
  const { campaign, fail } = useGame();
  const [v, setV] = useState(value.toFixed(2));
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { setV(value.toFixed(2)); }, [value]);
  const n = Number(v);
  const ok = Number.isFinite(n) && n >= 1 && n <= 10;
  const save = async () => {
    try {
      const r = await api.setCostMultiplier(campaign.id, n);
      setMsg(`Saved: ${r.multiplier.toFixed(2)}x`);
      onSaved();
    } catch (err) {
      fail(err);
    }
  };
  return (
    <>
      <div className="row">
        <label htmlFor="cost-mult">Cost multiplier for players: </label>
        <input id="cost-mult" type="number" min={1} max={10} step={0.01} style={{ width: "6em" }} value={v} onChange={(e) => { setV(e.target.value); setMsg(null); }} />
        <button className="primary" disabled={!ok || n.toFixed(2) === value.toFixed(2)} onClick={save}>Save multiplier</button>
        {msg && <span className="muted small">{msg}</span>}
      </div>
      <p className="muted small">
        Players see actual AI cost × this (1.00 = no markup, up to 10.00) for calls on <strong>your own xAI key</strong>. Calls on the
        server key use the Admin's server-key margin instead (never both). Applies to the session in progress and future ones; ended
        sessions keep the multiplier they were played at. {ok && n !== 1 && <>Example: $1.00 actual → players see {usd(n)}.</>}
        {!ok && <span className="danger"> Enter 1.00 to 10.00.</span>}
      </p>
    </>
  );
}

/** Host / Co-host: spending per session, the per-session cap. */
export function CostsPage() {
  const { campaign, isHost, isOwner, fail, costVersion, sessionsVersion } = useGame();
  const [data, setData] = useState<CampaignCostsResponse | null>(null);
  const reload = () => api.costs(campaign.id).then(setData, fail);
  const [cap, setCap] = useState<string>("");
  const [open, setOpen] = useState<number | null>(null);
  useEffect(() => {
    if (!isHost) return;
    api.costs(campaign.id).then((d) => {
      setData(d);
      setCap(d.capUsd === null ? "" : String(d.capUsd));
    }, fail);
  }, [campaign.id, isHost, fail, costVersion, sessionsVersion]);
  if (!isHost) return <main className="muted">Only the Host and Co-hosts can see costs.</main>;
  if (!data) return <main className="muted">Loading…</main>;
  const saveCap = async () => {
    try {
      await api.setCap(campaign.id, cap.trim() === "" ? null : Number(cap));
      setData(await api.costs(campaign.id));
    } catch (err) {
      fail(err);
    }
  };
  return (
    <main className="costs">
      <section className="panel">
        <h3>AI costs: {campaign.name}</h3>
        <p>
          Actual cost so far: <strong>{usd(data.actualUsd)}</strong>
          {data.outsideSessionsUsd > 0 && <span className="muted small"> (incl. {usd(data.outsideSessionsUsd)} between sessions, e.g. whisper replies)</span>}
          {data.totalUsd !== data.actualUsd && (
            <> · Players' price: <strong>{usd(data.totalUsd)}</strong> · Host margin: <strong>{usd(data.marginUsd)}</strong></>
          )}
        </p>
        <MultiplierEditor value={data.multiplier} onSaved={reload} />
        <p className="muted small">
          {campaign.useServerKey ? "This campaign may use the server's key when the Host has none. " : ""}
          Estimates from the Admin's price table; xAI's own billing is the final word. {isOwner ? "" : "The Host's key pays for this campaign."}
        </p>
        <div className="row">
          <label>Spending cap per session (actual cost, USD): </label>
          <input style={{ width: "6em" }} value={cap} placeholder="none" onChange={(e) => setCap(e.target.value)} />
          <button className="primary" onClick={saveCap}>Save cap</button>
        </div>
        <p className="muted small">
          The cap counts the <strong>actual</strong> AI cost (before the multiplier). The DM mentions it out loud at 80% and at 100% of the cap,
          without dollar amounts. Nothing stops: you decide whether to keep playing.
        </p>
      </section>
      <section className="panel">
        <h3>Sessions</h3>
        {data.sessions.length === 0 && <p className="muted">No sessions yet.</p>}
        <table className="simple">
          <thead><tr><th>#</th><th>Date</th><th>Actual</th><th>×</th><th>Players pay</th><th>Margin</th><th>Players</th><th>Each pays</th><th>Key</th><th /></tr></thead>
          <tbody>
            {data.sessions.map((s) => (
              <Fragment key={s.sessionId}>
                <tr>
                  <td>{s.number}</td>
                  <td>{new Date(s.startedAt).toLocaleDateString()}{!s.endedAt && <span className="tag ok">live</span>}</td>
                  <td>{usd(s.staff?.actualUsd ?? 0)}{s.capUsd !== null && (s.staff?.actualUsd ?? 0) >= s.capUsd && <span className="tag danger">over cap</span>}{s.unpriced && <span className="tag danger">unpriced</span>}</td>
                  <td className="small">{(s.staff?.multiplier ?? 1).toFixed(2)}</td>
                  <td>{usd(s.totalUsd)}</td>
                  <td>{usd(s.staff?.marginUsd ?? 0)}</td>
                  <td>{s.players.map((p) => p.name).join(", ") || "—"}</td>
                  <td>{usd(s.perPlayerUsd)}</td>
                  <td className="small">{s.keySource === "host" ? "Host's" : s.keySource === "server" ? "Server" : s.keySource === "mixed" ? "Mixed" : "—"}</td>
                  <td><button className="link" onClick={() => setOpen(open === s.sessionId ? null : s.sessionId)}>{open === s.sessionId ? "Hide" : "Details"}</button></td>
                </tr>
                {open === s.sessionId && (
                  <tr><td colSpan={10}><CostLines s={s} /></td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </section>
    </main>
  );
}

/** Shown to everyone at the table when a session ends: what it cost and each player's share. */
export function SessionCostPopup() {
  const { campaign, endedSessionId, clearEndedSession } = useGame();
  const [s, setS] = useState<SessionCost | null>(null);
  useEffect(() => {
    if (endedSessionId === null) return;
    let stop = false;
    // Give the scribe's final pass a moment so its cost is included.
    const t = setTimeout(() => {
      api.sessionCost(campaign.id, endedSessionId).then((r) => !stop && setS(r), () => clearEndedSession());
    }, 4000);
    return () => {
      stop = true;
      clearTimeout(t);
    };
  }, [campaign.id, endedSessionId, clearEndedSession]);
  if (endedSessionId === null || !s || s.sessionId !== endedSessionId) return null;
  const close = () => {
    setS(null);
    clearEndedSession();
  };
  return (
    <div className="modal-backdrop" onClick={close}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Session {s.number} is over</h3>
        <p>AI cost tonight: <strong>{usd(s.totalUsd)}</strong></p>
        {s.staff && (
          <p className="muted small">
            Host view: actual {usd(s.staff.actualUsd)}
            {s.staff.hostActualUsd > 0 && <> (your key {usd(s.staff.hostActualUsd)} × {s.staff.multiplier.toFixed(2)})</>}
            {s.staff.serverActualUsd > 0 && <> (server key {usd(s.staff.serverActualUsd)} + {s.staff.serverMarginPct}% margin)</>}
            {" "}= {usd(s.totalUsd)} players' price · margin {usd(s.staff.marginUsd)}
            {s.capUsd !== null && <> · cap {usd(s.capUsd)} (actual)</>}
          </p>
        )}
        {s.players.length > 0 && (
          <p>Split {s.players.length} way{s.players.length === 1 ? "" : "s"} ({s.players.map((p) => p.name).join(", ")}): <strong>{usd(s.perPlayerUsd)}</strong> each</p>
        )}
        <CostLines s={s} />
        <p className="muted small">Estimate from the price table; the memory scribe may add a little after this.</p>
        <NextSessionNote startedAt={s.startedAt} />
        <button className="primary" onClick={close}>Close</button>
      </div>
    </div>
  );
}

/** Server update countdown (everyone) and AI key failure (everyone; Hosts get the fix). */
export function SystemBanners() {
  const { session, campaign, isOwner, update } = useGame();
  const [status, setStatus] = useState<UpdateStatus | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    void api.updateStatus().then(setStatus, () => undefined);
    const t = setInterval(() => void api.updateStatus().then(setStatus, () => undefined), 60_000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (update) setStatus(update);
  }, [update]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const keyFailure = session?.keyFailure ?? campaign.keyFailure;
  const left = status?.phase === "countdown" && status.deadline ? Math.max(0, Date.parse(status.deadline) - now) : null;
  return (
    <>
      {left !== null && (
        <div className="system-banner update">
          Server update in <strong>{Math.floor(left / 60_000)}:{String(Math.floor((left % 60_000) / 1000)).padStart(2, "0")}</strong>. The session will be saved and closed; you can pick up right after.
        </div>
      )}
      {(status?.phase === "closing" || status?.phase === "applying") && (
        <div className="system-banner update">Updating the server now. This page reconnects by itself in a minute or two.</div>
      )}
      {keyFailure && (
        <div className="system-banner danger">
          <strong>The DM is paused: the AI key failed.</strong> {keyFailure}{" "}
          {isOwner ? <a href="#/settings/xai">Fix it in Settings → xAI</a> : "The Host has been told."}
        </div>
      )}
    </>
  );
}

/** Host / Co-host: tonight's running cost next to the cap (only when a cap is set). */
export function CapMeter() {
  const { campaign, isHost, playSession, costVersion } = useGame();
  const [s, setS] = useState<SessionCost | null>(null);
  useEffect(() => {
    if (!isHost || !playSession || campaign.sessionCapUsd === null) return;
    void api.sessionCost(campaign.id, playSession.id).then(setS, () => undefined);
  }, [isHost, playSession, campaign.id, campaign.sessionCapUsd, costVersion]);
  if (!isHost || !playSession || campaign.sessionCapUsd === null || !s) return null;
  const actual = s.staff?.actualUsd ?? s.totalUsd;
  const pct = Math.min(100, Math.round((actual / campaign.sessionCapUsd) * 100));
  return (
    <div className={`cap-meter ${pct >= 100 ? "over" : pct >= 80 ? "near" : ""}`}>
      Tonight (actual): {usd(actual)} of {usd(campaign.sessionCapUsd)} cap ({pct}%)
      {s.staff && s.staff.actualUsd !== s.totalUsd && <> · players see {usd(s.totalUsd)}</>} · <a href="#/costs">Costs</a>
    </div>
  );
}
